"use client";

import { cn } from "@/lib/utils";

export type IconBgColor =
  | "emerald"
  | "blue"
  | "purple"
  | "amber"
  | "gray"
  | "rose";

export interface MetaRowProps {
  /** Icon component to display */
  icon: React.ComponentType<{ className?: string }>;
  /** Background color for the icon container */
  iconBg?: IconBgColor;
  /** Optional label above the value */
  label?: string;
  /** The value to display - can be string or React node */
  value: string | React.ReactNode;
  /** Whether to truncate long text */
  truncate?: boolean;
  /** Number of lines to show before truncating (uses line-clamp) */
  lines?: 1 | 2 | 3;
  /** Additional CSS classes */
  className?: string;
}

const iconBgClasses: Record<IconBgColor, string> = {
  emerald: "bg-emerald-100 dark:bg-emerald-900/50",
  blue: "bg-blue-100 dark:bg-blue-900/50",
  purple: "bg-purple-100 dark:bg-purple-900/50",
  amber: "bg-amber-100 dark:bg-amber-900/50",
  gray: "bg-gray-100 dark:bg-gray-800",
  rose: "bg-rose-100 dark:bg-rose-900/50",
};

const iconColorClasses: Record<IconBgColor, string> = {
  emerald: "text-emerald-600 dark:text-emerald-400",
  blue: "text-blue-600 dark:text-blue-400",
  purple: "text-purple-600 dark:text-purple-400",
  amber: "text-amber-600 dark:text-amber-400",
  gray: "text-gray-600 dark:text-gray-400",
  rose: "text-rose-600 dark:text-rose-400",
};

const lineClampClasses = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
};

/**
 * MetaRow - Icon + label + value metadata display
 *
 * Use this component to display metadata with an icon in a colored box,
 * similar to the pattern used in CourseCard for location, etc.
 *
 * @example
 * <MetaRow
 *   icon={MapPin}
 *   iconBg="emerald"
 *   label="Location"
 *   value="123 Golf Course Dr, City, State"
 *   truncate
 *   lines={2}
 * />
 */
export function MetaRow({
  icon: Icon,
  iconBg = "gray",
  label,
  value,
  truncate = false,
  lines = 2,
  className,
}: MetaRowProps) {
  return (
    <div className={cn("flex items-start gap-3", className)}>
      {/* Icon container */}
      <div
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          iconBgClasses[iconBg]
        )}
      >
        <Icon className={cn("h-4 w-4", iconColorClasses[iconBg])} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {label && <p className="font-medium text-sm">{label}</p>}
        <div
          className={cn(
            "text-sm text-muted-foreground",
            truncate && lineClampClasses[lines]
          )}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default MetaRow;
